// ---------------------------------------------------------------------------
// CookbookBrowse.jsx
// Public cookbook discovery — available to guests and authenticated users.
// Authenticated users can follow cookbooks and see a "Following" shelf.
// ---------------------------------------------------------------------------

import React, { useState, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  BookOpen, Search, Heart, TrendingUp,
  BookMarked, Globe, Plus,
} from 'lucide-react';
import { usePublicCookbooks, useFollowedCookbooks } from '../../hooks/useCookbooks';
import { Button } from '../UI';
import { DIETARY_TAGS } from '../../constants/dietaryTags';
import { useNavigate } from 'react-router-dom';
import { getRecipeImageURL } from '../../hooks/useRecipes';

// ---------------------------------------------------------------------------
// CookbookCoverMosaic
// Reuses the exact mosaic / fallback logic from CookbookHub's CookbookCard.
// ---------------------------------------------------------------------------
const CookbookCoverMosaic = ({ coverImageURL, coverImages = [], name }) => {
  const imgs = coverImages.filter(Boolean);

  if (coverImageURL) {
    return <img src={coverImageURL} alt={name} className="w-full h-full object-cover" />;
  }

  if (imgs.length >= 4) {
    return (
      <div className="grid grid-cols-2 grid-rows-2 w-full h-full gap-px">
        {imgs.slice(0, 4).map((url, i) => (
          <div key={i} className="overflow-hidden">
            <img src={url} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    );
  }
  if (imgs.length === 3) {
    return (
      <div className="flex w-full h-full gap-px">
        <div className="flex-1 overflow-hidden">
          <img src={imgs[0]} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 flex flex-col gap-px">
          {imgs.slice(1).map((url, i) => (
            <div key={i} className="flex-1 overflow-hidden">
              <img src={url} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (imgs.length === 2) {
    return (
      <div className="flex w-full h-full gap-px">
        {imgs.map((url, i) => (
          <div key={i} className="flex-1 overflow-hidden">
            <img src={url} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    );
  }
  if (imgs.length === 1) {
    return <img src={imgs[0]} alt={name} className="w-full h-full object-cover" />;
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-sm">
      <BookOpen size={40} className="text-primary/20" />
      <p className="text-caption text-primary/25 font-medium">No recipes yet</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// PublicCookbookCard
// ---------------------------------------------------------------------------
const PublicCookbookCard = ({ cookbook, coverImages = [], onClick }) => {
  const count   = cookbook.recipeIds?.length || cookbook.recipeCount || 0;
  const follows = cookbook.followCount || 0;

  return (
    <div className="group relative cursor-pointer" onClick={onClick}>
      {/* Book depth shadow */}
      <div className="absolute inset-0 translate-x-2 translate-y-2 rounded-xl bg-black/15 blur-sm transition-all duration-300 group-hover:translate-x-3 group-hover:translate-y-3 group-hover:blur-md" />

      <div className="relative rounded-xl overflow-hidden bg-white transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl"
        style={{ border: '1px solid rgba(0,0,0,0.08)' }}
      >
        {/* Book spine */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-primary/50 via-primary/25 to-primary/50 rounded-l-xl z-10" />

        {/* Cover */}
        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary/12 via-primary/6 to-accent/8">
          <CookbookCoverMosaic
            coverImageURL={cookbook.coverImageURL}
            coverImages={coverImages}
            name={cookbook.name}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

          {/* Recipe count */}
          <div className="absolute bottom-sm left-sm">
            <span className="px-sm py-xs rounded-full bg-black/50 backdrop-blur-sm text-white text-[11px] font-semibold flex items-center gap-xs">
              <BookOpen size={10} />
              {count} recipe{count !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Follow count */}
          {follows > 0 && (
            <div className="absolute bottom-sm right-sm">
              <span className="px-sm py-xs rounded-full bg-black/50 backdrop-blur-sm text-white text-[11px] font-semibold flex items-center gap-xs">
                <Heart size={10} />
                {follows}
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-md">
          <h3 className="text-callout font-bold text-text-primary truncate">{cookbook.name}</h3>
          <p className="text-caption text-text-secondary mt-xs truncate">
            by {cookbook.ownerName || 'Nutrius User'}
          </p>
          {cookbook.description ? (
            <p className="text-caption text-text-tertiary line-clamp-2 mt-xs leading-relaxed">
              {cookbook.description}
            </p>
          ) : null}
          {cookbook.dietaryTags?.length > 0 && (
            <div className="flex flex-wrap gap-xs mt-sm">
              {cookbook.dietaryTags.slice(0, 3).map(tag => (
                <span
                  key={tag}
                  className="px-xs py-0.5 bg-primary/8 text-primary rounded-full text-[11px] font-semibold capitalize"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CookbookShelf — horizontal scroll row with a section title
// ---------------------------------------------------------------------------
const CookbookShelf = ({ title, icon: Icon, cookbooks, coverImagesMap, onSelect, emptyMessage }) => (
  <section>
    <div className="flex items-center gap-sm mb-lg">
      <Icon size={20} className="text-primary" />
      <h2 className="text-title-2 font-bold text-text-primary">{title}</h2>
    </div>

    {cookbooks.length === 0 ? (
      <p className="text-body text-text-tertiary py-md">{emptyMessage}</p>
    ) : (
      <div className="flex gap-xl overflow-x-auto pb-md -mx-4 px-4" style={{ scrollbarWidth: 'none' }}>
        {cookbooks.map(cb => (
          <div key={cb.id} className="flex-shrink-0 w-52">
            <PublicCookbookCard
              cookbook={cb}
              coverImages={coverImagesMap[cb.id] || []}
              onClick={() => onSelect(cb)}
            />
          </div>
        ))}
      </div>
    )}
  </section>
);

// ---------------------------------------------------------------------------
// CookbookBrowse
// ---------------------------------------------------------------------------
const CookbookBrowse = ({
  user,
  onSelectCookbook,
  onCreateCookbook,
}) => {
  const navigate = useNavigate();

  const [search,       setSearch]       = useState('');
  const [dietaryTag,   setDietaryTag]   = useState(null);

  const { cookbooks: publicCookbooks, loading } = usePublicCookbooks({ dietaryTag });
  const { cookbooks: followedCookbooks }        = useFollowedCookbooks(user?.uid || null);

  // Client-side search filter
  const filtered = useMemo(() => {
    if (!search.trim()) return publicCookbooks;
    const q = search.toLowerCase();
    return publicCookbooks.filter(cb =>
      cb.name?.toLowerCase().includes(q) ||
      cb.description?.toLowerCase().includes(q) ||
      cb.ownerName?.toLowerCase().includes(q)
    );
  }, [publicCookbooks, search]);

  // Featured = top 8 by followCount
  const featured = useMemo(() => filtered.slice(0, 8), [filtered]);
  // New = last 12 created
  const newest   = useMemo(
    () => [...filtered].sort((a, b) => {
      const ta = a.createdAt?.toDate?.() ?? new Date(a.createdAt ?? 0);
      const tb = b.createdAt?.toDate?.() ?? new Date(b.createdAt ?? 0);
      return tb - ta;
    }).slice(0, 12),
    [filtered]
  );

  // Prefetch cover image URLs from recipeIds — lightweight, first 4 per cookbook
  const [coverImagesMap, setCoverImagesMap] = React.useState({});
  React.useEffect(() => {
    if (!publicCookbooks.length) return;
    publicCookbooks.forEach(async cb => {
      if (coverImagesMap[cb.id] !== undefined || cb.coverImageURL) return;
      const ids = (cb.recipeIds || []).slice(0, 4);
      if (!ids.length) { setCoverImagesMap(prev => ({ ...prev, [cb.id]: [] })); return; }
      try {
        const snap = await getDocs(query(collection(db, 'recipes'), where('__name__', 'in', ids)));
        const urls = snap.docs
          .map(d => getRecipeImageURL({ ...d.data(), firestoreId: d.id }))
          .filter(Boolean);
        setCoverImagesMap(prev => ({ ...prev, [cb.id]: urls }));
      } catch {
        setCoverImagesMap(prev => ({ ...prev, [cb.id]: [] }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicCookbooks]);

  return (
    <div className="space-y-xxxl">

      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-md">
        <div>
          <h1 className="text-display font-rounded text-primary">Cookbooks</h1>
          <p className="text-body text-text-secondary mt-xs">
            Browse curated collections from the Nutrius community
          </p>
        </div>
        {user && (
          <Button variant="primary" onClick={onCreateCookbook}>
            <Plus size={16} className="mr-xs" />
            New Cookbook
          </Button>
        )}
      </div>

      {/* Guest notice */}
      {!user && (
        <div
          className="p-lg rounded-large bg-surface flex items-center gap-md"
          style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
        >
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <BookMarked size={20} className="text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-callout font-semibold text-text-primary">Browsing as Guest</p>
            <p className="text-caption text-text-secondary">
              Sign in to follow cookbooks, create your own, and add recipes to collections
            </p>
          </div>
          <div className="flex gap-sm flex-shrink-0">
            <Button variant="outline" size="small" onClick={() => navigate('/login')}>Sign In</Button>
            <Button variant="primary" size="small" onClick={() => navigate('/signup')}>Sign Up</Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search cookbooks by name, author, or description..."
          className="w-full pl-xl pr-lg py-md rounded-medium text-body text-text-primary bg-surface placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30"
          style={{ border: '1px solid rgba(var(--color-text-tertiary),0.2)' }}
        />
      </div>

      {/* Dietary filter pills */}
      <div>
        <p className="text-caption font-semibold text-text-tertiary uppercase tracking-wide mb-sm">
          Filter by diet
        </p>
        <div className="flex flex-wrap gap-xs">
          {DIETARY_TAGS.map(tag => (
            <button
              key={tag}
              onClick={() => setDietaryTag(prev => prev === tag ? null : tag)}
              className={`px-md py-xs rounded-full text-xs font-semibold transition-all ${
                dietaryTag === tag
                  ? 'bg-primary text-white'
                  : 'bg-primary/8 text-primary hover:bg-primary/15'
              }`}
            >
              {tag}
            </button>
          ))}
          {dietaryTag && (
            <button
              onClick={() => setDietaryTag(null)}
              className="text-xs text-text-tertiary hover:text-primary transition-colors ml-xs"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="text-center py-xxxl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-md" />
          <p className="text-body text-text-secondary">Loading cookbooks...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="rounded-large bg-surface p-xxxl text-center"
          style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
        >
          <BookOpen size={40} className="text-text-tertiary mx-auto mb-md" />
          <h3 className="text-title-3 text-text-primary mb-sm">No cookbooks yet</h3>
          <p className="text-body text-text-secondary mb-lg">
            {search || dietaryTag
              ? 'Try adjusting your search or filter.'
              : 'Be the first to publish a cookbook to the community.'}
          </p>
          {user && (
            <Button variant="primary" onClick={onCreateCookbook}>
              <Plus size={16} className="mr-xs" />
              Create Cookbook
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Following shelf — authenticated users only */}
          {user && followedCookbooks.length > 0 && (
            <CookbookShelf
              title="Following"
              icon={Heart}
              cookbooks={followedCookbooks}
              coverImagesMap={coverImagesMap}
              onSelect={onSelectCookbook}
              emptyMessage=""
            />
          )}

          {/* Trending shelf */}
          {!search && !dietaryTag && featured.length > 0 && (
            <CookbookShelf
              title="Trending"
              icon={TrendingUp}
              cookbooks={featured}
              coverImagesMap={coverImagesMap}
              onSelect={onSelectCookbook}
              emptyMessage="No cookbooks yet."
            />
          )}

          {/* All / search results grid */}
          <section>
            <div className="flex items-center gap-sm mb-lg">
              {search || dietaryTag ? (
                <>
                  <Search size={20} className="text-primary" />
                  <h2 className="text-title-2 font-bold text-text-primary">
                    Results
                    <span className="text-caption font-normal text-text-tertiary ml-sm">
                      ({filtered.length})
                    </span>
                  </h2>
                </>
              ) : (
                <>
                  <Globe size={20} className="text-primary" />
                  <h2 className="text-title-2 font-bold text-text-primary">All Cookbooks</h2>
                </>
              )}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-xl pt-sm">
              {(search || dietaryTag ? filtered : newest).map(cb => (
                <PublicCookbookCard
                  key={cb.id}
                  cookbook={cb}
                  coverImages={coverImagesMap[cb.id] || []}
                  onClick={() => onSelectCookbook(cb)}
                />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default CookbookBrowse;
